import { useEffect, useState, useRef } from "react"

function useWindowSize() {
    // Add a hasMounted state to track if component has mounted
    const [hasMounted, setHasMounted] = useState(false)
    const [windowSize, setWindowSize] = useState<{
        width: number
        height: number
    }>({
        width: 0,
        height: 0,
    })
    const rafRef = useRef<number | null>(null)

    useEffect(() => {
        setHasMounted(true)
        
        function handleResize() {
            // Debounce resize events using requestAnimationFrame for better iOS performance
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }
            rafRef.current = requestAnimationFrame(() => {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                })
            })
        }

        // Only add event listener on the client
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
        })
        window.addEventListener("resize", handleResize, { passive: true })

        return () => {
            window.removeEventListener("resize", handleResize)
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }
        }
    }, [])

    // Calculate these values only on the client side after mounting
    // Default values for server-side rendering
    const defaultValues = {
        width: 0,
        height: 0,
        isMobile: false,
        isTablet: false,
        isTabletOrSmaller: false,
        isDesktop: true // Default to desktop for SSR
    }

    // Return default values for SSR, real values for client
    if (!hasMounted) return defaultValues

    // Only calculate these on the client after mounting
    const isMobile = windowSize.width < 768
    const isTablet = windowSize.width >= 768 && windowSize.width < 1024
    const isDesktop = windowSize.width >= 1024

    return {
        width: windowSize.width,
        height: windowSize.height,
        isMobile,
        isTablet,
        isTabletOrSmaller: windowSize.width < 1024,
        isDesktop,
    }
}

export default useWindowSize
